import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Переводы
const TRANSLATIONS = {
  ru: {
    loading: "Загрузка...",
    subscription: "Подписка",
    yourSubscription: "Ваша подписка",
    subscriptionPlans: "Тарифы подписки",
    free: "Бесплатно",
    premium: "Премиум",
    vip: "VIP",
    currentPlan: "Текущий тариф",
    activate: "Активировать",
    activating: "Активация...",
    usageToday: "Использование сегодня:",
    messages: "Сообщений:",
    charactersAvailable: "Персонажей доступно:",
    close: "Закрыть",
    messagesPerDay: "сообщений/день",
    characters: "персонажей",
    perMonth: "/мес",
    failedToOpenBot: "Не удалось открыть чат с ботом. Попробуйте ещё раз."
  },
  en: {
    loading: "Loading...",
    subscription: "Subscription",
    yourSubscription: "Your subscription",
    subscriptionPlans: "Subscription plans",
    free: "Free",
    premium: "Premium",
    vip: "VIP",
    currentPlan: "Current plan",
    activate: "Activate",
    activating: "Activating...",
    usageToday: "Usage today:",
    messages: "Messages:",
    charactersAvailable: "Characters available:",
    close: "Close",
    messagesPerDay: "messages/day",
    characters: "characters",
    perMonth: "/month",
    failedToOpenBot: "Failed to open bot chat. Please try again."
  }
};

function SubscriptionButton({ user, userStatus, onSubscriptionUpdate, fullWidth = false, language = 'ru' }) {
  // Функция для получения перевода
  const t = (key) => TRANSLATIONS[language]?.[key] || TRANSLATIONS.ru[key];
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [showTariffs, setShowTariffs] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadSubscriptionInfo = useCallback(async () => {
    try {
      console.log('SubscriptionButton: Making API request to /api/subscription_info');
      const response = await axios.get(`/subscription_info?initData=${encodeURIComponent(user.initData)}`);
      console.log('SubscriptionButton: API response:', response.data);
      
      const normalizedTier = (response.data?.tier || 'free').toString().toLowerCase();
      setSubscriptionInfo({
        ...response.data,
        tier: normalizedTier,
        name: response.data?.name ?? getTierDisplayName(normalizedTier),
      });
    } catch (error) {
      console.error('SubscriptionButton: Failed to load subscription info:', error);
      if (userStatus?.subscription) {
        const normalizedTier = (userStatus.subscription.tier || 'free').toString().toLowerCase();
        setSubscriptionInfo({
          ...userStatus.subscription,
          tier: normalizedTier,
          name: userStatus.subscription.name ?? getTierDisplayName(normalizedTier),
          price: userStatus.subscription.price ?? 0,
          expires_at: userStatus.subscription.expires_at ?? null,
          is_active: true
        });
      } else {
        // Устанавливаем бесплатный тариф по умолчанию
        setSubscriptionInfo({
          tier: 'free',
          name: 'Free',
          daily_messages: 10,
          characters_unlocked: 3,
          price: 0,
          expires_at: null,
          is_active: true
        });
      }
    }
  }, [user?.initData, userStatus]);

  useEffect(() => {
    if (user?.initData) {
      console.log('SubscriptionButton: Loading subscription info for user:', user.initData);
      loadSubscriptionInfo();
    }
  }, [user?.initData, loadSubscriptionInfo]);

  const upgradeSubscription = async (tier) => {
    setLoading(true);
    try {
      // Вариант №1: сразу открываем чат бота с payload,
      // бот по payload отправляет invoice (Stars).
      const payload = tier === 'vip' ? 'pay_vip' : 'pay_premium';
      const link = `https://t.me/hotbump_bot?start=${payload}`;
      if (window.Telegram?.WebApp?.openTelegramLink) {
        window.Telegram.WebApp.openTelegramLink(link);
      } else {
        window.location.href = link;
      }
      setShowTariffs(false);
    } catch (error) {
      console.error('Failed to open bot chat:', error);
      alert(t('failedToOpenBot'));
    } finally {
      setLoading(false);
    }
  };


  const getTierDisplayName = (tier) => {
    const normalizedTier = (tier || 'free').toString().toLowerCase();
    switch (normalizedTier) {
      case 'premium': return t('premium');
      case 'vip': return t('vip');
      case 'free': return t('free');
      default: return normalizedTier;
    }
  };

  if (!subscriptionInfo) {
    console.log('SubscriptionButton: No subscription info, showing loading...');
    return (
      <div className={`bg-burgundy-700 text-white px-4 py-2 rounded-lg text-sm font-medium ${fullWidth ? 'w-full text-center' : ''}`}>
        {t('loading')}
      </div>
    );
  }

  console.log('SubscriptionButton: Rendering with subscription info:', subscriptionInfo);

  return (
    <div className="relative">
      {/* Кнопка подписки */}
      <button
        onClick={() => setShowTariffs(!showTariffs)}
        className={`${
          fullWidth 
            ? 'w-full bg-burgundy-700 hover:bg-burgundy-600 text-white px-6 py-4 rounded-lg font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 justify-center' 
            : 'bg-burgundy-700 hover:bg-burgundy-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors'
        } flex items-center space-x-2`}
      >
        <span>{fullWidth ? t('yourSubscription') : t('subscription')}: {getTierDisplayName(subscriptionInfo.tier)}</span>
        <span className="text-xs opacity-75">▼</span>
      </button>

      {/* Выпадающее меню с тарифами */}
      {showTariffs && (
        <div className="absolute top-full right-0 mt-2 w-80 bg-dark-800 rounded-lg shadow-2xl border border-burgundy-800 z-50">
          <div className="p-4">
            <h3 className="text-lg font-bold text-gray-100 mb-4">{t('subscriptionPlans')}</h3>
            
            {/* Таблица тарифов */}
            <div className="space-y-3 mb-4">
              {/* Бесплатный тариф */}
              <div className={`p-3 rounded-lg border-2 ${subscriptionInfo.tier === 'free' ? 'border-burgundy-500 bg-burgundy-50' : 'border-gray-700 bg-dark-800'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">🆓</span>
                    <span className={`font-medium ${subscriptionInfo.tier === 'free' ? 'text-burgundy-800' : 'text-gray-100'}`}>{t('free')}</span>
                  </div>
                  <span className={`text-sm ${subscriptionInfo.tier === 'free' ? 'text-burgundy-600' : 'text-gray-400'}`}>{t('free')}</span>
                </div>
                <div className={`text-xs space-y-1 ${subscriptionInfo.tier === 'free' ? 'text-gray-700' : 'text-gray-400'}`}>
                  <div>• 10 {t('messagesPerDay')}</div>
                  <div>• 3 {t('characters')}</div>
                </div>
                {subscriptionInfo.tier === 'free' && (
                  <div className="mt-2 text-xs text-burgundy-600 font-medium">{t('currentPlan')}</div>
                )}
              </div>

              {/* Премиум тариф */}
              <div className={`p-3 rounded-lg border-2 ${subscriptionInfo.tier === 'premium' ? 'border-burgundy-500 bg-burgundy-50' : 'border-gray-700 bg-dark-800'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">⭐</span>
                    <span className={`font-medium ${subscriptionInfo.tier === 'premium' ? 'text-burgundy-800' : 'text-gray-100'}`}>{t('premium')}</span>
                  </div>
                  <span className={`text-sm ${subscriptionInfo.tier === 'premium' ? 'text-burgundy-600' : 'text-gray-400'}`}>
                    199⭐{t('perMonth')} <span className="line-through opacity-60 ml-1">399⭐</span>
                  </span>
                </div>
                <div className={`text-xs space-y-1 ${subscriptionInfo.tier === 'premium' ? 'text-gray-700' : 'text-gray-400'}`}>
                  <div>• 50 {t('messagesPerDay')}</div>
                  <div>• 7 {t('characters')}</div>
                </div>
                {subscriptionInfo.tier === 'premium' ? (
                  <div className="mt-2 text-xs text-burgundy-600 font-medium">{t('currentPlan')}</div>
                ) : (
                  <button
                    onClick={() => upgradeSubscription('premium')}
                    disabled={loading}
                    className="mt-2 w-full bg-burgundy-600 hover:bg-burgundy-500 disabled:bg-gray-600 text-white text-xs py-1 px-2 rounded transition-colors"
                  >
                    {loading ? t('activating') : t('activate')}
                  </button>
                )}
              </div>

              {/* VIP тариф */}
              <div className={`p-3 rounded-lg border-2 ${subscriptionInfo.tier === 'vip' ? 'border-burgundy-500 bg-burgundy-50' : 'border-gray-700 bg-dark-800'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">👑</span>
                    <span className={`font-medium ${subscriptionInfo.tier === 'vip' ? 'text-burgundy-800' : 'text-gray-100'}`}>{t('vip')}</span>
                  </div>
                  <span className={`text-sm ${subscriptionInfo.tier === 'vip' ? 'text-burgundy-600' : 'text-gray-400'}`}>
                    499⭐{t('perMonth')} <span className="line-through opacity-60 ml-1">999⭐</span>
                  </span>
                </div>
                <div className={`text-xs space-y-1 ${subscriptionInfo.tier === 'vip' ? 'text-gray-700' : 'text-gray-400'}`}>
                  <div>• ∞ {t('messagesPerDay')}</div>
                  <div>• ∞ {t('characters')}</div>
                </div>
                {subscriptionInfo.tier === 'vip' ? (
                  <div className="mt-2 text-xs text-burgundy-600 font-medium">{t('currentPlan')}</div>
                ) : (
                  <button
                    onClick={() => upgradeSubscription('vip')}
                    disabled={loading}
                    className="mt-2 w-full bg-burgundy-700 hover:bg-burgundy-600 disabled:bg-gray-600 text-white text-xs py-1 px-2 rounded transition-colors"
                  >
                    {loading ? t('activating') : t('activate')}
                  </button>
                )}
              </div>
            </div>

            {/* Информация о текущем использовании */}
            {userStatus && (
              <div className="bg-dark-700 p-3 rounded-lg border border-burgundy-800">
                <h4 className="text-sm font-medium text-gray-100 mb-2">{t('usageToday')}</h4>
                <div className="text-xs text-gray-400">
                  <div>{t('messages')} {userStatus.daily_messages} / {subscriptionInfo.daily_messages === -1 ? '∞' : subscriptionInfo.daily_messages}</div>
                  <div>{t('charactersAvailable')} {subscriptionInfo.characters_unlocked === -1 ? '∞' : subscriptionInfo.characters_unlocked}</div>
                </div>
              </div>
            )}

            {/* Кнопка закрытия */}
            <button
              onClick={() => setShowTariffs(false)}
              className="mt-3 w-full text-gray-400 hover:text-burgundy-400 text-xs py-1"
            >
              {t('close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubscriptionButton;
